import { ReactElement, useEffect } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Typography,
  alpha,
} from '@mui/material'
import { Check, Close } from '@mui/icons-material'
import { Theme, useTheme } from '@mui/material/styles'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import DangerousHtml from 'src/v3/components/dangerous-html'
import {
  fetchCourseTitleScreen,
  fetchManageCourseVersion,
  fetchManageCourseV2Content,
  selectCourseTitleScreen,
  selectCourseTitleScreenStatus,
  selectCourseVersion,
  selectCourseVersionStatus,
  selectCourseV2Content,
  selectCourseV2ContentStatus,
} from 'src/v3/manage/manage.slice'
import {
  CourseV2Accreditation,
  CourseV2Content,
} from 'src/v3/classroom-v2/types'

type CourseDetailModalProps = {
  courseID: string | null
  courseName: string
  onClose: () => void
}

// Accreditation rendering helpers — duplicated from course-overview.tsx so
// this modal stays self-contained without extracting shared utilities.

type AccreditationColorKey = keyof Theme['palette']['accreditationColors']

type AccreditationRowInfo = {
  badgeLabel: string
  colorKey: AccreditationColorKey
  label: string | null
  detail: string | null
}

let ceBrokerStateAbbreviation: Record<string, string> = {
  Florida: 'FL',
  Kentucky: 'KY',
  Tennessee: 'TN',
}

let accreditationRowInfoByKind: {
  [K in CourseV2Accreditation['kind']]: (
    accreditation: Extract<CourseV2Accreditation, { kind: K }>,
  ) => AccreditationRowInfo
} = {
  capce: (accreditation) => ({
    badgeLabel: 'CAPCE',
    colorKey: 'capce',
    label: null,
    detail:
      [accreditation.classType, accreditation.topicArea]
        .filter(Boolean)
        .join(' · ') || null,
  }),
  ancc: () => ({
    badgeLabel: 'ANCC',
    colorKey: 'ancc',
    label: null,
    detail: null,
  }),
  ceBroker: (accreditation) => ({
    badgeLabel:
      ceBrokerStateAbbreviation[accreditation.state] ??
      accreditation.state.slice(0, 2).toUpperCase(),
    colorKey: 'ceBroker',
    label: `${accreditation.state} (CE Broker)`,
    detail: null,
  }),
  virginia: (accreditation) => ({
    badgeLabel: 'VA',
    colorKey: 'virginia',
    label: 'Virginia',
    detail:
      [
        accreditation.areaNumberALS ? 'ALS' : null,
        accreditation.areaNumberBLS ? 'BLS' : null,
      ]
        .filter(Boolean)
        .join(' · ') || null,
  }),
  newJersey: () => ({
    badgeLabel: 'NJ',
    colorKey: 'newJersey',
    label: 'New Jersey',
    detail: null,
  }),
  newYork: (accreditation) => ({
    badgeLabel: 'NY',
    colorKey: 'newYork',
    label: 'New York',
    detail: accreditation.topicArea || null,
  }),
  accredited: (accreditation) => ({
    badgeLabel: 'Accredited',
    colorKey: 'accredited',
    label: null,
    detail: accreditation.courseCode || null,
  }),
  notAccredited: (accreditation) => ({
    badgeLabel: 'Not Accredited',
    colorKey: 'notAccredited',
    label: null,
    detail: accreditation.courseCode || null,
  }),
}

let accreditationRowInfo = (
  accreditation: CourseV2Accreditation,
): AccreditationRowInfo =>
  accreditationRowInfoByKind[accreditation.kind](accreditation as never)

let AccreditationBadge = ({
  label,
  color,
}: {
  label: string
  color: string
}) => (
  <Box
    className="inline-flex items-center justify-center shrink-0 px-2 py-1 rounded font-bold uppercase whitespace-nowrap text-[0.7rem] tracking-[0.5px]"
    sx={{ bgcolor: alpha(color, 0.16), color }}
  >
    {label}
  </Box>
)

let AccreditationRow = ({
  accreditation,
}: {
  accreditation: CourseV2Accreditation
}) => {
  let theme = useTheme()
  let row = accreditationRowInfo(accreditation)
  let color = theme.palette.accreditationColors[row.colorKey]

  return (
    <Box className="flex items-center gap-3 py-3">
      <AccreditationBadge label={row.badgeLabel} color={color} />
      {row.label && <Typography variant="body1">{row.label}</Typography>}
      {row.detail ? (
        <Chip label={row.detail} variant="outlined" size="small" />
      ) : null}
    </Box>
  )
}

let V2CourseDetail = ({ content }: { content: CourseV2Content }) => {
  let capce = content.accreditation.find(
    (a): a is Extract<CourseV2Accreditation, { kind: 'capce' }> =>
      a.kind === 'capce',
  )

  return (
    <Box className="p-6">
      <Typography variant="body2" color="text.secondary" className="mb-4">
        {content.length} CE {content.length === 1 ? 'Hour' : 'Hours'}
        {content.category ? ` · ${content.category}` : ''}
      </Typography>

      {capce && (
        <Box className="mb-4">
          <Typography variant="overline" color="text.secondary">
            Class Type / Topic Area
          </Typography>
          <Typography variant="body1">
            {[capce.classType, capce.topicArea].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      )}

      {content.accreditation.length > 0 && (
        <Box className="mb-4">
          <Typography variant="overline" color="text.secondary">
            Accreditations
          </Typography>
          {content.accreditation.map((accreditation, i) => (
            <AccreditationRow key={i} accreditation={accreditation} />
          ))}
        </Box>
      )}

      {content.instructors.length > 0 && (
        <Box className="mb-4">
          <Typography variant="overline" color="text.secondary">
            {content.instructors.length > 1 ? 'Instructors' : 'Instructor'}
          </Typography>
          <Typography variant="body1">
            {content.instructors
              .map((i) =>
                i.credentials ? `${i.name}, ${i.credentials}` : i.name,
              )
              .join(' · ')}
          </Typography>
        </Box>
      )}

      {content.syllabus?.description && (
        <Box className="mb-4">
          <Typography variant="overline" color="text.secondary">
            Description
          </Typography>
          <Typography variant="body2">
            {content.syllabus.description}
          </Typography>
        </Box>
      )}

      {content.syllabus?.objectives &&
        content.syllabus.objectives.length > 0 && (
          <Box>
            <Typography variant="overline" color="text.secondary">
              Learning Objectives
            </Typography>
            <List dense disablePadding>
              {content.syllabus.objectives.map((objective, i) => (
                <ListItem key={i} className="px-0 py-1.5 gap-3 items-start">
                  <Box
                    className="flex items-center justify-center shrink-0 w-5 h-5 rounded-full mt-0.5"
                    sx={{
                      bgcolor: 'success.main',
                      color: 'success.contrastText',
                    }}
                  >
                    <Check sx={{ fontSize: 14 }} />
                  </Box>
                  <Typography variant="body1">{objective}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
    </Box>
  )
}

export default function CourseDetailModal({
  courseID,
  courseName,
  onClose,
}: CourseDetailModalProps): ReactElement {
  let dispatch = useAppDispatch()
  let titleScreen = useAppSelector(selectCourseTitleScreen(courseID ?? ''))
  let titleScreenStatus = useAppSelector(
    selectCourseTitleScreenStatus(courseID ?? ''),
  )
  let courseVersion = useAppSelector(selectCourseVersion(courseID ?? ''))
  let versionStatus = useAppSelector(selectCourseVersionStatus(courseID ?? ''))
  let v2Content = useAppSelector(selectCourseV2Content(courseID ?? ''))
  let v2ContentStatus = useAppSelector(
    selectCourseV2ContentStatus(courseID ?? ''),
  )

  let isV2Course =
    versionStatus === 'fulfilled' ? courseVersion?.publishedVersion === 2 : null

  // Fetch course version when the modal opens
  useEffect(() => {
    if (courseID && versionStatus === 'uninitialized') {
      dispatch(fetchManageCourseVersion(courseID))
    }
  }, [courseID, versionStatus, dispatch])

  // Fetch v1 title screen once we know the course is v1
  useEffect(() => {
    if (
      courseID &&
      isV2Course === false &&
      titleScreenStatus === 'uninitialized'
    ) {
      dispatch(fetchCourseTitleScreen(courseID))
    }
  }, [courseID, isV2Course, titleScreenStatus, dispatch])

  // Fetch v2 content once we know the course is v2
  useEffect(() => {
    if (
      courseID &&
      isV2Course === true &&
      v2ContentStatus === 'uninitialized'
    ) {
      dispatch(fetchManageCourseV2Content(courseID))
    }
  }, [courseID, isV2Course, v2ContentStatus, dispatch])

  let isLoading =
    versionStatus === 'uninitialized' ||
    versionStatus === 'pending' ||
    (isV2Course === false &&
      (titleScreenStatus === 'uninitialized' ||
        titleScreenStatus === 'pending')) ||
    (isV2Course === true &&
      (v2ContentStatus === 'uninitialized' || v2ContentStatus === 'pending'))

  return (
    <Dialog open={!!courseID} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box className="flex items-center justify-between">
          <Typography variant="h6">{courseName}</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        )}
        {!isLoading && versionStatus === 'rejected' && (
          <Box className="p-6">
            <Typography color="error">
              Failed to load course details.
            </Typography>
          </Box>
        )}
        {!isLoading &&
          isV2Course === false &&
          titleScreenStatus === 'rejected' && (
            <Box className="p-6">
              <Typography color="error">
                Failed to load course details.
              </Typography>
            </Box>
          )}
        {!isLoading &&
          isV2Course === false &&
          titleScreenStatus === 'fulfilled' &&
          titleScreen && (
            <Box className="p-6">
              <Typography
                sx={{ fontFamily: 'smileAndWave' }}
                variant="h4"
                className="mb-4"
              >
                {titleScreen.name}
              </Typography>
              <DangerousHtml htmlVal={titleScreen.content} isEncoded={true} />
            </Box>
          )}
        {!isLoading &&
          isV2Course === true &&
          v2ContentStatus === 'rejected' && (
            <Box className="p-6">
              <Typography color="error">
                Failed to load course details.
              </Typography>
            </Box>
          )}
        {!isLoading &&
          isV2Course === true &&
          v2ContentStatus === 'fulfilled' &&
          v2Content && <V2CourseDetail content={v2Content} />}
      </DialogContent>
    </Dialog>
  )
}
